import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const propertyVisitModule: ChecklistModuleDefinition = {
  code: 'property_visit',
  name: 'Property Visit',
  description: 'On-site inspection and walkthrough',
  categories: [
    {
      code: 'visit_planning',
      name: 'Visit Planning',
      items: [
        {
          code: 'schedule_visit',
          title: 'Schedule property visit',
          description: 'Arrange on-site inspection',
          isGroupTask: true,
          dataFields: [
            { key: 'visit_date', label: 'Visit Date', type: 'date' },
            { key: 'visit_time', label: 'Visit Time', type: 'string' },
            { key: 'meeting_contact', label: 'Meeting Contact', type: 'string' },
            { key: 'access_instructions', label: 'Access Instructions', type: 'string' },
          ],
        },
        {
          code: 'visit_preparation',
          title: 'Visit preparation checklist',
          description: 'Items to bring/do before visit',
          isGroupTask: true,
          dataFields: [
            { key: 'items_to_bring', label: 'Items to Bring', type: 'multiselect', options: [
              { value: 'camera', label: 'Camera' },
              { value: 'drone', label: 'Drone' },
              { value: 'gps', label: 'GPS Device' },
              { value: 'maps', label: 'Property Maps' },
              { value: 'boots', label: 'Boots/Outdoor Gear' },
              { value: 'water', label: 'Water/Snacks' },
              { value: 'checklist', label: 'Printed Checklist' },
              { value: 'tape_measure', label: 'Tape Measure' },
              { value: 'binoculars', label: 'Binoculars' },
              { value: 'soil_probe', label: 'Soil Probe' },
            ]},
            { key: 'research_complete', label: 'Pre-Visit Research Complete', type: 'boolean' },
            { key: 'questions_prepared', label: 'Questions Prepared', type: 'boolean' },
          ],
        },
        {
          code: 'partners_attending',
          title: 'Partners attending',
          description: 'Which partners will attend visit',
          isGroupTask: true,
          dataFields: [
            { key: 'partners_attending', label: 'Partners Attending', type: 'array' },
            { key: 'roles_assigned', label: 'Roles Assigned', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'first_impressions',
      name: 'First Impressions',
      items: [
        {
          code: 'initial_impression',
          title: 'Initial impression',
          description: 'First reaction upon arrival',
          isGroupTask: true,
          dataFields: [
            { key: 'gut_feeling', label: 'Gut Feeling', type: 'select', options: [
              { value: 'love_it', label: 'Love It' },
              { value: 'like_it', label: 'Like It' },
              { value: 'neutral', label: 'Neutral' },
              { value: 'concerns', label: 'Have Concerns' },
              { value: 'not_right', label: 'Not Right' },
            ]},
            { key: 'matches_expectations', label: 'Matches Expectations', type: 'boolean' },
            { key: 'initial_notes', label: 'Initial Notes', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Love it - exceeds expectations' },
            { flag: 'red', condition: 'Significant concerns on arrival' },
          ],
        },
        {
          code: 'access_experience',
          title: 'Access/driving experience',
          description: 'Experience getting to property',
          isGroupTask: true,
          dataFields: [
            { key: 'ease_of_access', label: 'Ease of Access', type: 'select', options: [
              { value: 'easy', label: 'Easy - No Issues' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'difficult', label: 'Difficult' },
              { value: 'very_difficult', label: 'Very Difficult' },
            ]},
            { key: 'road_conditions_observed', label: 'Road Conditions Observed', type: 'string' },
            { key: 'signage', label: 'Signage Adequate', type: 'boolean' },
          ],
        },
        {
          code: 'surroundings_observed',
          title: 'Surroundings observed',
          description: 'What you noticed on the way',
          isGroupTask: true,
          dataFields: [
            { key: 'neighborhood_impression', label: 'Neighborhood Impression', type: 'string' },
            { key: 'notable_neighbors', label: 'Notable Neighbors Observed', type: 'array' },
            { key: 'development_observed', label: 'Development Observed', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'property_walkthrough',
      name: 'Property Walkthrough',
      items: [
        {
          code: 'boundaries_walked',
          title: 'Walk property boundaries',
          description: 'Walk and observe boundary lines',
          isGroupTask: true,
          dataFields: [
            { key: 'boundaries_walked', label: 'Boundaries Walked', type: 'select', options: [
              { value: 'all', label: 'All Boundaries' },
              { value: 'most', label: 'Most Boundaries' },
              { value: 'some', label: 'Some Boundaries' },
              { value: 'none', label: 'None' },
            ]},
            { key: 'markers_found', label: 'Markers/Pins Found', type: 'boolean' },
            { key: 'boundary_concerns', label: 'Boundary Concerns', type: 'array' },
          ],
        },
        {
          code: 'features_inspected',
          title: 'Key features inspected',
          description: 'What was physically inspected',
          isGroupTask: true,
          dataFields: [
            { key: 'features_inspected', label: 'Features Inspected', type: 'multiselect', options: [
              { value: 'house', label: 'House/Residence' },
              { value: 'outbuildings', label: 'Outbuildings' },
              { value: 'well', label: 'Well' },
              { value: 'septic', label: 'Septic' },
              { value: 'pond', label: 'Pond(s)' },
              { value: 'creek', label: 'Creek/Stream' },
              { value: 'timber', label: 'Timber' },
              { value: 'pastures', label: 'Pastures' },
              { value: 'fencing', label: 'Fencing' },
              { value: 'roads', label: 'Internal Roads' },
            ]},
            { key: 'inspection_notes', label: 'Inspection Notes', type: 'string' },
          ],
        },
        {
          code: 'areas_accessed',
          title: 'Areas accessed',
          description: 'What parts of property were seen',
          isGroupTask: true,
          dataFields: [
            { key: 'coverage_percent', label: 'Coverage %', type: 'number', unit: 'percent' },
            { key: 'inaccessible_areas', label: 'Inaccessible Areas', type: 'array' },
            { key: 'additional_visit_needed', label: 'Additional Visit Needed', type: 'boolean' },
          ],
        },
        {
          code: 'building_sites',
          title: 'Building site evaluation',
          description: 'Identify potential building locations',
          isGroupTask: true,
          dataFields: [
            { key: 'sites_identified', label: 'Sites Identified', type: 'number' },
            { key: 'preferred_site', label: 'Preferred Site', type: 'string' },
            { key: 'site_notes', label: 'Site Notes', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'documentation',
      name: 'Documentation',
      items: [
        {
          code: 'photos_taken',
          title: 'Photos/video taken',
          description: 'Visual documentation',
          isGroupTask: true,
          dataFields: [
            { key: 'photos_count', label: 'Photos Taken', type: 'number' },
            { key: 'video_taken', label: 'Video Taken', type: 'boolean' },
            { key: 'drone_footage', label: 'Drone Footage', type: 'boolean' },
            { key: 'photo_subjects', label: 'Photo Subjects', type: 'array' },
          ],
        },
        {
          code: 'gps_points',
          title: 'GPS points recorded',
          description: 'Important locations marked',
          isGroupTask: false,
          dataFields: [
            { key: 'points_recorded', label: 'Points Recorded', type: 'number' },
            { key: 'point_types', label: 'Point Types', type: 'array' },
          ],
        },
        {
          code: 'measurements_taken',
          title: 'Measurements taken',
          description: 'Physical measurements recorded',
          isGroupTask: false,
          dataFields: [
            { key: 'measurements', label: 'Measurements Recorded', type: 'array' },
          ],
        },
      ],
    },
    {
      code: 'visit_conclusions',
      name: 'Visit Conclusions',
      items: [
        {
          code: 'surprises_discoveries',
          title: 'Surprises and discoveries',
          description: 'Unexpected findings',
          isGroupTask: true,
          dataFields: [
            { key: 'positive_surprises', label: 'Positive Surprises', type: 'array' },
            { key: 'negative_surprises', label: 'Negative Surprises', type: 'array' },
            { key: 'verification_needed', label: 'Items to Verify', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Major positive discoveries' },
            { flag: 'red', condition: 'Major negative discoveries' },
          ],
        },
        {
          code: 'questions_answered',
          title: 'Questions answered',
          description: 'What we learned',
          isGroupTask: true,
          dataFields: [
            { key: 'questions_answered', label: 'Questions Answered', type: 'array' },
            { key: 'remaining_questions', label: 'Remaining Questions', type: 'array' },
          ],
        },
        {
          code: 'partner_impressions',
          title: 'Partner impressions',
          description: 'Each partner feedback',
          isGroupTask: true,
          dataFields: [
            { key: 'partner_feedback', label: 'Partner Feedback', type: 'array' },
            { key: 'consensus', label: 'Consensus', type: 'select', options: [
              { value: 'all_positive', label: 'All Positive' },
              { value: 'mostly_positive', label: 'Mostly Positive' },
              { value: 'mixed', label: 'Mixed' },
              { value: 'mostly_negative', label: 'Mostly Negative' },
              { value: 'all_negative', label: 'All Negative' },
            ]},
          ],
        },
        {
          code: 'post_visit_rating',
          title: 'Post-visit assessment',
          description: 'Overall assessment after visit',
          isGroupTask: true,
          dataFields: [
            { key: 'overall_rating', label: 'Overall Rating', type: 'select', options: [
              { value: '5', label: '5 - Excellent, move forward' },
              { value: '4', label: '4 - Good, proceed carefully' },
              { value: '3', label: '3 - Okay, needs more investigation' },
              { value: '2', label: '2 - Concerns, hesitant' },
              { value: '1', label: '1 - Not interested' },
            ]},
            { key: 'proceed_recommendation', label: 'Recommendation', type: 'select', options: [
              { value: 'proceed', label: 'Proceed Aggressively' },
              { value: 'proceed_caution', label: 'Proceed with Caution' },
              { value: 'more_info', label: 'Need More Information' },
              { value: 'pass', label: 'Pass on This Property' },
            ]},
            { key: 'next_steps', label: 'Recommended Next Steps', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Strong recommendation to proceed' },
            { flag: 'red', condition: 'Recommendation to pass' },
          ],
        },
      ],
    },
    {
      code: 'follow_up',
      name: 'Follow-Up Actions',
      items: [
        {
          code: 'follow_up_visit',
          title: 'Follow-up visit needed',
          description: 'Plan additional visits',
          isGroupTask: true,
          dataFields: [
            { key: 'needed', label: 'Follow-up Needed', type: 'boolean' },
            { key: 'purpose', label: 'Purpose', type: 'string' },
            { key: 'who_should_attend', label: 'Who Should Attend', type: 'array' },
            { key: 'specialists_needed', label: 'Specialists Needed', type: 'array' },
          ],
        },
        {
          code: 'action_items',
          title: 'Post-visit action items',
          description: 'Tasks resulting from visit',
          isGroupTask: true,
          dataFields: [
            { key: 'action_items', label: 'Action Items', type: 'array' },
            { key: 'priority_items', label: 'Priority Items', type: 'array' },
          ],
        },
      ],
    },
  ],
};

